import { capitalize, get, isFunction, isBoolean, map, isNil, includes, isInteger, isNumber, isEmpty, isNaN } from "lodash"
import pluralize from "pluralize"
import { VIEW_TYPES } from "helpers/base"

export const XLSX_NUMBER_FORMAT = "0.????????"
export const XLSX_PERCENT_FORMAT = "0.00#%"

// Create a download link for any type of data
export function fileDownload(data, filename, mime, bom) {
  const blobData = (typeof bom !== "undefined") ? [bom, data] : [data]
  const blob = new Blob(blobData, { type: mime || "application/octet-stream" })

  const blobURL = window.URL.createObjectURL(blob)
  const tempLink = document.createElement("a")
  tempLink.style.display = "none"
  tempLink.href = blobURL
  tempLink.setAttribute("download", filename)

  document.body.appendChild(tempLink)
  tempLink.addEventListener("click", (e) => {
    e.stopPropagation()
  })
  tempLink.click()

  document.body.removeChild(tempLink)
  window.URL.revokeObjectURL(blobURL)
}

// Allows up to 8 decimal points but will truncate superfluous zeros
export function toFixedNumber(number, precision = 8) {
  if (isNil(number)) {
    return "-"
  }
  return Number(parseFloat(number).toFixed(precision))
}

function getEquityColumnHeader(type, viewType) {
  const isActive = viewType === VIEW_TYPES.ACTIVE
  let equityColumnHeader = isActive ? "Active Equity (%)" : "Equity (%)"

  if (type === "Performance") {
    equityColumnHeader = isActive ? "Avg Active Equity (%)" : "Avg Equity (%)"
  }
  return equityColumnHeader
}

export function getExcelValue(value, { percent } = {}) {
  if (isNil(value) || isNaN(value)) { return "" }

  if (percent) {
    return { t: "n", v: value, z: XLSX_PERCENT_FORMAT }
  }

  // No specific format for integers otherwise we get values like: 12.
  if (value === 0 || isInteger(value)) {
    return { t: "n", v: value }
  }

  return { t: "n", v: value, z: XLSX_NUMBER_FORMAT }
}

export function computeGroupedContributorsCSV({
  type,
  portfolioName,
  selected,
  selectedDate,
  viewType,
  group,
  net = false,
  showEconomicExposure = false,
}) {
  const lastHeader = net ? type : `${type} (%)`
  const fileName = `${portfolioName} - ${selected?.label} ${group.name} - ${capitalize(viewType)} ${type} ${selectedDate}`
  const titles = [
    "Identifier",
    "MIC",
    "Bloomberg Ticker",
    "Country",
    "Asset Class",
    "Currency",
    "Industry",
    getEquityColumnHeader(type, viewType),
  ]
  showEconomicExposure && titles.push("Economic exposure")
  selected && titles.push(lastHeader)
  return {
    fileName,
    type,
    data: [
      titles,
      ...group.contributors.map((item) => {
        const row = [
          item.ticker || item.id,
          item.exchange,
          item.bloombergTicker,
          item.country,
          item.assetClass,
          item.currency,
          get(item, "classification.name"),
          getExcelValue(item.size, { percent: true }),
        ]
        showEconomicExposure && row.push(getExcelValue(item.economicExposure))
        selected && row.push(getExcelValue(get(item, selected.field), { percent: !net }))
        return row
      }),
    ],
  }
}

export function computeContributorsGroupCSV({
  type,
  portfolioName,
  selected,
  selectedDate,
  viewType,
  contributorsGrouping,
  data,
  net = false,
  showEconomicExposure = false,
}) {
  const lastHeader = net ? type : `${type} (%)`
  const groupingLabel = get(contributorsGrouping, "label")
  const fileName = `${portfolioName} - ${selected?.label} Grouped By ${pluralize(groupingLabel, data.length)} - ${capitalize(viewType)} ${type} ${selectedDate}`
  const titles = [groupingLabel, getEquityColumnHeader(type, viewType)]
  showEconomicExposure && titles.push("Economic exposure")
  selected && titles.push(lastHeader)
  return {
    fileName,
    type,
    data: [
      titles,
      ...data.map((item) => {
        const row = [
          item.name || item.id,
          getExcelValue(item.totalPercentEquity, { percent: true }),
        ]
        showEconomicExposure && row.push(getExcelValue(item.economicExposure))
        selected && row.push(getExcelValue(get(item, selected.field), { percent: !net }))
        return row
      }),
    ],
  }
}

export function computeContributorsCSV({
  type,
  portfolioName,
  selected,
  selectedDate,
  viewType,
  data,
  net = false,
  showEconomicExposure = false,
}) {
  const lastHeader = net ? type : `${type} (%)`
  const fileName = `${portfolioName} ${selected?.label} ${capitalize(viewType)} ${type} ${selectedDate}`
  const titles = [
    "Identifier",
    "MIC",
    "Bloomberg Ticker",
    "Country",
    "Asset Class",
    "Currency",
    "Industry",
    getEquityColumnHeader(type, viewType),
  ]
  showEconomicExposure && titles.push("Economic exposure")
  selected && titles.push(lastHeader)
  return {
    fileName,
    type,
    data: [
      titles,
      ...data.map((item) => {
        const row = [
          item.ticker || item.id,
          item.exchange,
          item.bloombergTicker,
          item.country,
          item.assetClass,
          item.currency,
          item.classification,
          getExcelValue(item.size, { percent: true }),
        ]
        showEconomicExposure && row.push(getExcelValue(item.economicExposure))
        selected && row.push(getExcelValue(get(item, selected.field), { percent: !net }))
        return row
      }),
    ],
  }
}

export const csvLocation = {
  asset: "Asset",
  contributors: "Contributors",
  litmus: "Litmus",
  optimization: "Optimization",
}

export function downloadCSV(fileName, csvString, type, csvLocation) {
  const a = document.createElement("a")
  a.href = `data:text/csv,${encodeURI(csvString)}`
  a.download = `${fileName}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()

  const payload = { type }

  window.analytics.track(`${csvLocation} CSV - Download`, payload)
}

export async function downloadXlsx(fileName, data, { type, location, track } = {}) {
  try {
    const bodyContent = get(data, "sheets") ? { sheets: data.sheets } : { rows: data }
    const response = await fetch("/excelDownload", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bodyContent),
    })
    if (!response.ok) {
      return false
    }
    const blob = await response.blob()
    fileDownload(blob, `${fileName}.xlsx`)
  } catch {
    return false
  }

  if (isFunction(track)) {
    track()
  } else {
    window.analytics.track(`${location} CSV - Download`, { type })
  }
  return true
}

// Item specific overrides global prop
function shouldDisplayItemInPct(item, percents = false) {
  const itemPct = get(item, "percentage")
  if (isBoolean(itemPct)) { return itemPct }

  return percents
}

export function computeCSVData(data, items, { percents } = {}) {
  if (isEmpty(data) || isEmpty(items)) {
    return []
  }

  return [
    ["Date (YYYY-MM-DD)", ...items.map(item => shouldDisplayItemInPct(item, percents) ? `${item.label} (%)` : item.label)],
    ...data.map(datum => ([
      datum.date.format("YYYY-MM-DD"),
      ...items.map(item => getExcelValue(get(datum, item.id, 0), { percent: shouldDisplayItemInPct(item, percents) })),
    ])),
  ]
}

function makeLitmusRowMapFn(factors) {
  const makeGetFactorValue = row => factor => {
    const value = factor.getValue(row, factor.id)
    const formatted = factor.formatter(value, row.type, true)

    if (includes(formatted, "%")) {
      return { t: "n", v: value, z: XLSX_PERCENT_FORMAT }
    }

    if (formatted && !isNumber(formatted) && formatted.match(/[^0-9-.]/gi)) {
      return { t: "s", v: value, w: formatted }
    }

    return getExcelValue(value)
  }

  return (row) => [
    row.ticker || row.key,
    get(row, "bloombergTicker"),
    get(row, "exchange"),
    get(row, "country"),
    get(row, "assetClass"),
    get(row, "currency"),
    get(row, "classification"),
    ...map(factors, makeGetFactorValue(row)),
  ]
}

export function computeLitmusCSVData(factors, rows) {
  const csvHeaders = [
    "Ticker",
    "Bloomberg Ticker",
    "Exchange",
    "Country",
    "Asset Class",
    "Currency",
    "Industry",
    ...map(factors, "label"),
  ]

  return [csvHeaders, ...map(rows, makeLitmusRowMapFn(factors))]
}
